import { Injectable } from '@angular/core';
import { EDeliverableType } from '@enums/campaign.enum';
import { ICtaAnalyticsDeliverables } from '@interfaces/campaign-analytics-api.interface';

/** Derived from the analytics interface — auto-expands when the interface grows. */
export type TDeliverableCollectionKey = keyof ICtaAnalyticsDeliverables;

export interface IDeliverableCollectionConfig {
  collectionKey: TDeliverableCollectionKey;
  type: EDeliverableType;
}

@Injectable({
  providedIn: 'root',
})
export class DeliverableUtilitiesService {
  /**
   * `Record<keyof ICtaAnalyticsDeliverables, EDeliverableType>` — TypeScript requires all keys.
   * Adding a field to `ICtaAnalyticsDeliverables` without adding an entry here is a compile error.
   */
  private readonly deliverableCollectionTypeMap: Record<keyof ICtaAnalyticsDeliverables, EDeliverableType> = {
    audioTrackings: EDeliverableType.audioTracking,
    captions: EDeliverableType.caption,
    chatbot: EDeliverableType.chatBot,
    chatbotMultis: EDeliverableType.chatbotMulti,
    contents: EDeliverableType.content,
    customs: EDeliverableType.custom,
    descriptions: EDeliverableType.description,
    gamePlay: EDeliverableType.gamePlay,
    hashtags: EDeliverableType.hashTag,
    mentions: EDeliverableType.mention,
    messages: EDeliverableType.message,
    productPlacements: EDeliverableType.productPlacement,
    showcases: EDeliverableType.showcase,
    talkingPointAis: EDeliverableType.talkingPointAi,
    talkingPoints: EDeliverableType.talkingPoint,
    textTrackings: EDeliverableType.textTracking,
    titles: EDeliverableType.title,
    visuals: EDeliverableType.visual,
  };

  private readonly knownCollectionKeys = new Set<string>(Object.keys(this.deliverableCollectionTypeMap));

  private readonly warnedKeys = new Set<string>();

  public getDeliverableCollectionConfigs(
    ctaAnalyticsDeliverables: ICtaAnalyticsDeliverables,
  ): IDeliverableCollectionConfig[] {
    this.warnMissingConfigs(ctaAnalyticsDeliverables);

    return (
      Object.entries(this.deliverableCollectionTypeMap) as Array<[keyof ICtaAnalyticsDeliverables, EDeliverableType]>
    ).map(([collectionKey, type]) => ({ collectionKey, type }));
  }

  private warnMissingConfigs(ctaAnalyticsDeliverables: ICtaAnalyticsDeliverables): void {
    for (const key of Object.keys(ctaAnalyticsDeliverables)) {
      if (this.knownCollectionKeys.has(key) || this.warnedKeys.has(key)) {
        continue;
      }

      const value = (ctaAnalyticsDeliverables as unknown as Record<string, unknown>)[key];

      if (Array.isArray(value) && value.length > 0) {
        this.warnedKeys.add(key);
        // eslint-disable-next-line no-console
        console.error(
          `[DeliverableUtilitiesService] Unknown deliverable collection key "${key}" found in ctaAnalytics. Add it to deliverableCollectionConfigs in DeliverableUtilitiesService.`,
        );
      }
    }
  }

  public isTrackingDeliverable(type: EDeliverableType | string): boolean {
    const normalizedType: string = type.toString().toLowerCase();
    const trackingTypes: string[] = [
      EDeliverableType.audioTracking.toLowerCase(),
      EDeliverableType.textTracking.toLowerCase(),
    ];

    if (trackingTypes.includes(normalizedType)) {
      return true;
    }

    return normalizedType.includes('tracking');
  }
}
